import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { erpMe, erpDashboard } from '../../services/erpApi';
import './erp.css';
import ErpLogin from './ErpLogin';
import { Sidebar } from './layout/Sidebar';
import { Topbar } from './layout/Topbar';
import { Dashboard } from './dashboard/Dashboard';
import { Masters } from './pages/Masters';
import { Products } from './pages/Products';
import { Parties } from './pages/Parties';
import { Inventory } from './pages/Inventory';
import { UsersPage } from './pages/UsersPage';
import { ComingSoon } from './pages/ComingSoon';
import { Quotations } from './sales/Quotations';
import { SalesOrders } from './sales/SalesOrders';
import { Invoices } from './sales/Invoices';
import { Dispatch } from './sales/Dispatch';
import { Indents } from './purchase/Indents';
import { PurchaseOrders } from './purchase/PurchaseOrders';
import { Grn } from './purchase/Grn';

// Page registry rendered via createElement (the visual-edits babel plugin overflows on deep JSX component graphs).
const ROUTES = {
  'sales/quotations': Quotations, 'sales/orders': SalesOrders, 'sales/invoices': Invoices, 'sales/dispatch': Dispatch,
  'purchase/indents': Indents, 'purchase/orders': PurchaseOrders, 'purchase/grn': Grn,
  products: Products, customers: Parties, vendors: Parties, inventory: Inventory, masters: Masters, administration: UsersPage,
};
const FIRST = { sales: 'sales/quotations', purchase: 'purchase/indents' };

const Page = ({ view, user, dash, go }) => {
  const [root, sub] = view.split('/');
  if (view.startsWith('soon/') || root === 'settings') return React.createElement(ComingSoon, { view: root === 'settings' ? 'soon/settings' : view, go });
  const key = ROUTES[view] ? view : (ROUTES[root] ? root : FIRST[root]);
  const Comp = ROUTES[key];
  if (!Comp) return React.createElement(Dashboard, { user, data: dash, go });
  const props = { user, go, kind: root === 'vendors' ? 'vendors' : 'customers', sub: root === 'inventory' ? (sub === 'ledger' ? 'ledger' : 'position') : (sub || 'companies') };
  return React.createElement(Comp, props);
};

const ErpApp = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [dash, setDash] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const view = pathname.replace(/^\/erp\/?/, '') || 'dashboard';

  const go = useCallback((v) => navigate(`/erp/${v}`), [navigate]);

  useEffect(() => {
    const t = localStorage.getItem('erp_token');
    if (!t) { setChecking(false); return; }
    erpMe().then((r) => setUser(r.data)).catch(() => localStorage.removeItem('erp_token')).finally(() => setChecking(false));
  }, []);

  const loadDash = useCallback(() => {
    erpDashboard().then((r) => setDash(r.data)).catch(() => toast.error('Failed to load dashboard'));
  }, []);
  useEffect(() => { if (user) loadDash(); }, [user, view, loadDash]);

  if (checking) return <div className="erp-root min-h-screen grid place-items-center" style={{ background: 'var(--erp-navy)' }}><div className="erp-skeleton h-10 w-40 opacity-30" /></div>;
  if (!user) return <ErpLogin onAuthed={(u) => { setUser(u); go('dashboard'); }} />;

  const logout = () => { localStorage.removeItem('erp_token'); setUser(null); setDash(null); };

  return (
    <div className="erp-root min-h-screen flex">
      <Sidebar view={view} go={go} collapsed={collapsed} />
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar user={user} go={go} onToggleSidebar={() => setCollapsed((c) => !c)} alertCount={dash?.alerts?.length || 0} onLogout={logout} />
        <main className="flex-1 p-4 sm:p-6 lg:p-7" data-testid="erp-main">
          <Page key={view} view={view} user={user} dash={dash} go={go} />
        </main>
        <footer className="px-6 py-3 flex flex-wrap items-center justify-between gap-2 text-xs border-t bg-white" style={{ borderColor: 'var(--erp-line)', color: 'var(--erp-muted)' }}>
          <span className="font-bold" style={{ color: 'var(--erp-navy)' }}>NAMOH TOOLINGS <span className="mx-1 font-normal">|</span> ERP</span>
          <span>Version 1.0 · {new Date().getFullYear()} · <span className="font-semibold">We Listen We Innovate</span></span>
        </footer>
      </div>
    </div>
  );
};

export default ErpApp;
